// 📄 pages/Proyectos/components/AprendizajeSection.tsx
// "Aprendizaje" (plan-proyectos-generales.md, G7, etapa 1): guías para los
// usuarios finales del proyecto, con pasos (texto + imagen), adjuntos y un
// video. El equipo las escribe y las publica; el rol cliente ve solo las
// publicadas (lo exigen las reglas). Etapa 2 (pendiente): enlace de acceso
// para usuarios finales que no son miembros del proyecto.
import React, { useEffect, useMemo, useState } from 'react';
import {
  GraduationCap, Plus, RefreshCw, X, Save, Search, Pencil, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
  ImagePlus, Paperclip, Video, FileText, ArrowLeft
} from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import {
  Guia, GuiaFormData, PasoGuia, getGuias, createGuia, updateGuia, setEstadoGuia, deleteGuia, subirArchivoGuia,
  nuevoPaso, urlVideoEmbebible, LIMITES_GUIA
} from '../../../services/guiaService.ts';
import { clasificacionDe } from '../modulosConfig.ts';
import { getDisplayName } from '../../../utils/displayName.ts';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-400 mb-1";

export default function AprendizajeSection() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const [guias, setGuias] = useState<Guia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('todas');
  const [viendo, setViendo] = useState<string | null>(null);
  const [editando, setEditando] = useState<Guia | 'nueva' | null>(null);
  const [borrando, setBorrando] = useState<Guia | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      setGuias(await getGuias(projectId, esCliente));
    } catch (err: any) {
      setError(err.message || 'No se pudieron cargar las guías.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, esCliente]);

  const categorias = useMemo(() => Array.from(new Set(guias.map((g) => g.categoria).filter(Boolean))).sort(), [guias]);
  const visibles = guias.filter((g) => {
    if (categoria !== 'todas' && (g.categoria || '') !== categoria) return false;
    const q = busqueda.trim().toLowerCase();
    return !q || g.titulo.toLowerCase().includes(q) || g.descripcion.toLowerCase().includes(q);
  });

  if (!project || !user) return null;
  const nombre = getDisplayName(user, profile);
  const actual = guias.find((g) => g.id === viendo) || null;

  const ejecutar = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo completar la acción.');
    }
  };

  if (actual) {
    return (
      <GuiaVista
        guia={actual}
        puedeEditar={!esCliente}
        onVolver={() => setViendo(null)}
        onEditar={() => setEditando(actual)}
        onPublicar={() => ejecutar(() => setEstadoGuia(projectId, actual.id, actual.estado === 'Publicada' ? 'Borrador' : 'Publicada', nombre))}
        onBorrar={() => setBorrando(actual)}
        extra={
          <>
            {editando && (
              <GuiaEditor
                projectId={projectId}
                guia={editando === 'nueva' ? null : editando}
                categorias={[...clasificacionDe(project).opciones, ...categorias]}
                onClose={() => setEditando(null)}
                onGuardar={(data) => ejecutar(async () => {
                  if (editando === 'nueva') await createGuia(projectId, data, user.uid, nombre);
                  else await updateGuia(projectId, editando.id, data, nombre);
                  setEditando(null);
                })}
              />
            )}
            {borrando && (
              <ConfirmDialog
                title="¿Eliminar esta guía?"
                message={`Se eliminará "${borrando.titulo}". Esta acción no se puede deshacer.`}
                confirmLabel="Eliminar"
                danger
                onConfirm={() => ejecutar(async () => { await deleteGuia(projectId, borrando.id); setBorrando(null); setViendo(null); })}
                onCancel={() => setBorrando(null)}
              />
            )}
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}
          </>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">
          {esCliente ? 'Guías publicadas para los usuarios del sistema.' : 'Guías para los usuarios finales: cómo usar lo que se entrega. El cliente ve solo las publicadas.'}
        </p>
        {!esCliente && (
          <button onClick={() => setEditando('nueva')} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white">
            <Plus className="w-4 h-4" /> Nueva guía
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar guía" className="w-full bg-zinc-800 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-xs text-white focus:outline-none" />
        </div>
        {categorias.length > 0 && (
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none">
            <option value="todas">Todas las categorías</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando guías...</p>
        </div>
      ) : visibles.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <GraduationCap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">{guias.length === 0 ? (esCliente ? 'Todavía no hay guías publicadas.' : 'Todavía no hay guías. Crea la primera, por ejemplo "Cómo tomar un pedido".') : 'Ninguna guía coincide con la búsqueda.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibles.map((g) => (
            <button key={g.id} onClick={() => setViendo(g.id)} className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-5 space-y-2 transition-all">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-semibold text-white">{g.titulo}</h4>
                {!esCliente && (
                  <span className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${g.estado === 'Publicada' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-gray-400 bg-white/5 border-white/15'}`}>{g.estado}</span>
                )}
              </div>
              {g.descripcion && <p className="text-xs text-gray-400 line-clamp-2">{g.descripcion}</p>}
              <p className="text-[11px] text-gray-500 flex items-center gap-2 flex-wrap">
                {g.categoria && <span>{g.categoria}</span>}
                <span>{g.pasos.length} paso{g.pasos.length === 1 ? '' : 's'}</span>
                {g.videoUrl && <span className="flex items-center gap-1"><Video className="w-3 h-3" /> video</span>}
                {g.adjuntos.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {g.adjuntos.length}</span>}
              </p>
            </button>
          ))}
        </div>
      )}

      {editando && (
        <GuiaEditor
          projectId={projectId}
          guia={editando === 'nueva' ? null : editando}
          categorias={[...clasificacionDe(project).opciones, ...categorias]}
          onClose={() => setEditando(null)}
          onGuardar={(data) => ejecutar(async () => {
            if (editando === 'nueva') await createGuia(projectId, data, user.uid, nombre);
            else await updateGuia(projectId, editando.id, data, nombre);
            setEditando(null);
          })}
        />
      )}
    </div>
  );
}

function GuiaVista({ guia, puedeEditar, onVolver, onEditar, onPublicar, onBorrar, extra }: {
  guia: Guia; puedeEditar: boolean; onVolver: () => void; onEditar: () => void; onPublicar: () => void; onBorrar: () => void; extra: React.ReactNode;
}) {
  const embebible = guia.videoUrl ? urlVideoEmbebible(guia.videoUrl) : null;
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onVolver} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Todas las guías</button>
        {puedeEditar && (
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${guia.estado === 'Publicada' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' : 'text-gray-400 bg-white/5 border-white/15'}`}>{guia.estado}</span>
            <button onClick={onPublicar} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${guia.estado === 'Publicada' ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-[#fff]'}`}>
              {guia.estado === 'Publicada' ? <><EyeOff className="w-3.5 h-3.5" /> Volver a borrador</> : <><Eye className="w-3.5 h-3.5" /> Publicar</>}
            </button>
            <button onClick={onEditar} title="Editar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
            <button onClick={onBorrar} title="Eliminar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {extra}

      <div>
        <h2 className="text-2xl font-semibold text-white">{guia.titulo}</h2>
        {guia.categoria && <p className="text-xs text-gray-500 mt-1">{guia.categoria}</p>}
        {guia.descripcion && <p className="text-sm text-gray-300 mt-3 whitespace-pre-wrap">{guia.descripcion}</p>}
      </div>

      {guia.videoUrl && (embebible ? (
        <div className="aspect-video rounded-2xl overflow-hidden border border-white/10">
          <iframe src={embebible} title={guia.titulo} className="w-full h-full" allowFullScreen />
        </div>
      ) : (
        <a href={guia.videoUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-400 hover:underline"><Video className="w-4 h-4" /> Ver video</a>
      ))}

      <ol className="space-y-4">
        {guia.pasos.map((p, i) => (
          <li key={p.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <div className="flex-1 min-w-0 space-y-2">
                {p.titulo && <h4 className="text-sm font-semibold text-white">{p.titulo}</h4>}
                {p.contenido && <p className="text-sm text-gray-300 whitespace-pre-wrap">{p.contenido}</p>}
                {p.imagen && <img src={p.imagen.url} alt={p.titulo || `Paso ${i + 1}`} className="rounded-xl border border-white/10 max-h-[420px] object-contain" />}
              </div>
            </div>
          </li>
        ))}
      </ol>

      {guia.adjuntos.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400">Adjuntos</p>
          <div className="flex flex-wrap gap-2">
            {guia.adjuntos.map((a) => (
              <a key={a.url} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-blue-300 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-1.5">
                <FileText className="w-3.5 h-3.5" /> {a.name}
              </a>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-600">Última actualización: {guia.actualizadoPorNombre}{guia.actualizadoEn?.toDate ? ` · ${guia.actualizadoEn.toDate().toLocaleString()}` : ''}</p>
    </div>
  );
}

function GuiaEditor({ projectId, guia, categorias, onClose, onGuardar }: {
  projectId: string; guia: Guia | null; categorias: string[]; onClose: () => void; onGuardar: (data: GuiaFormData) => Promise<void>;
}) {
  const [data, setData] = useState<GuiaFormData>(guia
    ? { titulo: guia.titulo, descripcion: guia.descripcion, categoria: guia.categoria, videoUrl: guia.videoUrl, pasos: guia.pasos.length ? guia.pasos : [nuevoPaso()], adjuntos: guia.adjuntos }
    : { titulo: '', descripcion: '', categoria: '', videoUrl: '', pasos: [nuevoPaso()], adjuntos: [] });
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setPaso = (id: string, cambios: Partial<PasoGuia>) =>
    setData((d) => ({ ...d, pasos: d.pasos.map((p) => (p.id === id ? { ...p, ...cambios } : p)) }));
  const mover = (i: number, delta: number) => setData((d) => {
    const pasos = [...d.pasos];
    const j = i + delta;
    if (j < 0 || j >= pasos.length) return d;
    [pasos[i], pasos[j]] = [pasos[j], pasos[i]];
    return { ...d, pasos };
  });

  const subir = async (clave: string, file: File | undefined, alSubir: (a: any) => void) => {
    if (!file) return;
    setSubiendo(clave);
    setError(null);
    try {
      alSubir(await subirArchivoGuia(projectId, file));
    } catch (err: any) {
      setError(err.message || 'No se pudo subir el archivo.');
    } finally {
      setSubiendo(null);
    }
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(data);
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar la guía.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{guia ? 'Editar guía' : 'Nueva guía'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={guardar} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Título*</label>
              <input autoFocus value={data.titulo} onChange={(e) => setData({ ...data, titulo: e.target.value })} maxLength={150} placeholder="ej. Cómo tomar un pedido desde el celular" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Categoría</label>
              <input value={data.categoria} onChange={(e) => setData({ ...data, categoria: e.target.value })} list="categorias-guia" maxLength={60} placeholder="ej. Meseros, Caja" className={inputClass} />
              <datalist id="categorias-guia">{Array.from(new Set(categorias)).map((c) => <option key={c} value={c} />)}</datalist>
            </div>
          </div>
          <div>
            <label className={labelClass}>Descripción</label>
            <textarea value={data.descripcion} onChange={(e) => setData({ ...data, descripcion: e.target.value })} rows={2} maxLength={2000} className={`${inputClass} resize-none`} placeholder="Para quién es y qué va a aprender" />
          </div>
          <div>
            <label className={labelClass}>Video (opcional)</label>
            <input value={data.videoUrl} onChange={(e) => setData({ ...data, videoUrl: e.target.value })} placeholder="https://youtube.com/... o un enlace de Drive" className={inputClass} />
          </div>

          <div className="space-y-3">
            <p className={labelClass}>Pasos</p>
            {data.pasos.map((p, i) => (
              <div key={p.id} className="bg-black/20 border border-white/10 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</span>
                  <input value={p.titulo} onChange={(e) => setPaso(p.id, { titulo: e.target.value })} maxLength={150} placeholder="Título del paso (opcional)" className={`${inputClass} py-1.5`} />
                  <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} title="Subir" className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => mover(i, 1)} disabled={i === data.pasos.length - 1} title="Bajar" className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => setData((d) => ({ ...d, pasos: d.pasos.filter((x) => x.id !== p.id) }))} title="Quitar paso" className="p-1 text-gray-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea value={p.contenido} onChange={(e) => setPaso(p.id, { contenido: e.target.value })} rows={3} maxLength={4000} placeholder="Qué hacer en este paso" className={`${inputClass} resize-none`} />
                <div className="flex items-center gap-3">
                  {p.imagen ? (
                    <>
                      <img src={p.imagen.url} alt="" className="h-16 rounded-lg border border-white/10" />
                      <button type="button" onClick={() => setPaso(p.id, { imagen: null })} className="text-xs text-gray-400 hover:text-red-400">Quitar imagen</button>
                    </>
                  ) : (
                    <label className="flex items-center gap-1.5 text-xs text-blue-300 cursor-pointer hover:text-blue-200">
                      {subiendo === p.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} Agregar imagen
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => subir(p.id, e.target.files?.[0], (img) => setPaso(p.id, { imagen: img }))} />
                    </label>
                  )}
                </div>
              </div>
            ))}
            {data.pasos.length < LIMITES_GUIA.pasos && (
              <button type="button" onClick={() => setData((d) => ({ ...d, pasos: [...d.pasos, nuevoPaso()] }))} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300">
                <Plus className="w-3.5 h-3.5" /> Agregar paso
              </button>
            )}
          </div>

          <div className="space-y-2">
            <p className={labelClass}>Adjuntos (PDF, manuales, plantillas…)</p>
            <div className="flex flex-wrap gap-2">
              {data.adjuntos.map((a) => (
                <span key={a.url} className="flex items-center gap-1.5 text-xs text-gray-300 bg-white/5 border border-white/10 rounded-lg pl-3 pr-1 py-1">
                  <FileText className="w-3.5 h-3.5" /> {a.name}
                  <button type="button" onClick={() => setData((d) => ({ ...d, adjuntos: d.adjuntos.filter((x) => x.url !== a.url) }))} className="p-0.5 rounded hover:bg-white/10"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            {data.adjuntos.length < LIMITES_GUIA.adjuntos && (
              <label className="inline-flex items-center gap-1.5 text-xs text-blue-300 cursor-pointer hover:text-blue-200">
                {subiendo === 'adjunto' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Adjuntar archivo (máx. 15 MB)
                <input type="file" className="hidden" onChange={(e) => subir('adjunto', e.target.files?.[0], (a) => setData((d) => ({ ...d, adjuntos: [...d.adjuntos, a] })))} />
              </label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
            <button type="submit" disabled={guardando || !!subiendo} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-white">
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar como {guia?.estado === 'Publicada' ? 'publicada' : 'borrador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
